import { useQuery } from '@tanstack/react-query';
import { Link, useRouter, useLocation } from '@tanstack/react-router';
import {
  LayoutDashboard,
  BarChart3,
  DoorOpen,
  Settings,
  Menu,
  LogOut,
  User as UserIcon,
  ChevronLeft,
  ChevronRight,
  X,
  Trash2,
  Eye,
  EyeOff
} from 'lucide-react';
import * as React from 'react';

import { Avatar, AvatarFallback } from '~/components/ui/avatar';
import { Button } from '~/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '~/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '~/components/ui/sheet';
import { useAppSettings } from '~/hooks/use-app-settings';
import { useActionBar } from '~/lib/action-bar';
import { api } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import { usePresentationMode } from '~/lib/presentation-mode';
import { isPublicAppRoute } from '~/lib/route-auth';
import { cn } from '~/lib/utils';

const RECENT_ROOMS_LIMIT = 10;
const RECENT_ROOMS_EVENT = 'open-dataroom:recent-rooms-updated';

const navItems = [
  {
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    roles: ['owner', 'admin', 'viewer']
  },
  {
    name: 'Rooms',
    href: '/rooms',
    icon: DoorOpen,
    roles: ['owner', 'admin', 'viewer']
  },
  {
    name: 'Reports',
    href: '/reports',
    icon: BarChart3,
    roles: ['owner', 'admin']
  },
  { name: 'Bin', href: '/bin', icon: Trash2, roles: ['owner', 'admin'] },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    roles: ['owner', 'admin', 'viewer']
  }
];

function AppLogo() {
  return (
    <svg
      className="h-6 w-6 text-primary"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <path d="M7 6h10v12H7" />
      <circle cx="9" cy="12" r="1.2" fill="currentColor" stroke="none" />
    </svg>
  );
}

function CompanyName() {
  const { data: settings } = useAppSettings();
  const name = settings?.companyName ?? 'Open Dataroom';
  return (
    <span className="min-w-0 flex-1 truncate text-sm font-semibold tracking-tight">
      {name}
    </span>
  );
}

function recentRoomsStorageKey(userId: string) {
  return `open-dataroom:recent-rooms:v1:${userId}`;
}

function readRecentRoomIds(userId: string): string[] {
  if (typeof window === 'undefined') return [];

  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(recentRoomsStorageKey(userId)) ?? '[]'
    );
    if (!Array.isArray(parsed)) return [];

    const seen = new Set<string>();
    const roomIds: string[] = [];
    for (const value of parsed) {
      if (typeof value !== 'string' || seen.has(value)) continue;
      seen.add(value);
      roomIds.push(value);
      if (roomIds.length === RECENT_ROOMS_LIMIT) break;
    }
    return roomIds;
  } catch {
    return [];
  }
}

function roomIdFromPathname(pathname: string): string | null {
  const [, encodedRoomId] = pathname.match(/^\/rooms\/([^/]+)/) ?? [];
  if (!encodedRoomId || encodedRoomId === 'new') return null;

  try {
    return decodeURIComponent(encodedRoomId);
  } catch {
    return encodedRoomId;
  }
}

function stringArraysEqual(a: string[], b: string[]) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

function recordRecentRoomOpen(userId: string, roomId: string): string[] {
  const current = readRecentRoomIds(userId);
  if (current[0] === roomId) return current;

  const next = [roomId, ...current.filter((id) => id !== roomId)].slice(
    0,
    RECENT_ROOMS_LIMIT
  );
  try {
    window.localStorage.setItem(
      recentRoomsStorageKey(userId),
      JSON.stringify(next)
    );
    window.dispatchEvent(new Event(RECENT_ROOMS_EVENT));
  } catch {
    // Recent rooms are a convenience; navigation should keep working if storage is unavailable.
  }
  return next;
}

interface NavLinkProps {
  item: (typeof navItems)[0];
  onClick?: () => void;
  showLabel: boolean;
}

function NavLink({ item, onClick, showLabel }: NavLinkProps) {
  const location = useLocation();
  const isActive =
    item.href === '/dashboard'
      ? location.pathname === '/dashboard'
      : item.href === '/rooms'
        ? location.pathname === '/rooms' ||
          location.pathname === '/rooms/' ||
          location.pathname.startsWith('/rooms/')
        : item.href === '/settings'
          ? location.pathname === '/settings' ||
            location.pathname === '/settings/' ||
            location.pathname.startsWith('/settings/')
          : location.pathname === item.href ||
            location.pathname.startsWith(`${item.href}/`);
  const Icon = item.icon;
  return (
    <Link
      to={item.href}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-md px-2 py-2 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        !showLabel && 'justify-center gap-0 px-2',
        isActive
          ? 'bg-accent text-accent-foreground'
          : 'text-muted-foreground hover:bg-accent/80 hover:text-accent-foreground'
      )}
    >
      <Icon className="h-4 w-4 shrink-0 opacity-80" />
      {showLabel && <span className="truncate">{item.name}</span>}
    </Link>
  );
}

interface SidebarContentProps {
  onNavigate?: () => void;
  expanded?: boolean;
  mode?: 'rail' | 'drawer';
  sidebarCollapsed: boolean;
  recentRooms: Array<{ id: string; name: string }>;
  filteredNav: typeof navItems;
  initials: string;
}

function SidebarContent({
  onNavigate,
  expanded,
  mode = 'rail',
  sidebarCollapsed,
  recentRooms,
  filteredNav,
  initials
}: SidebarContentProps) {
  const location = useLocation();
  const isDrawer = mode === 'drawer';
  const showNavText = isDrawer || expanded === true ? true : !sidebarCollapsed;

  return (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col bg-card',
        isDrawer && 'border-0 shadow-none'
      )}
    >
      {isDrawer ? (
        <div className="flex h-14 shrink-0 items-center gap-2 border-b px-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center">
            <AppLogo />
          </div>
          <CompanyName />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 text-muted-foreground"
            aria-label="Close menu"
            onClick={() => onNavigate?.()}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        /* Rail: brand lives in the global header; nav starts here. */
        <div className="h-2 shrink-0 lg:h-3" aria-hidden />
      )}

      <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto px-2 py-3">
        <div className="space-y-0.5">
          {filteredNav.map((item) => (
            <NavLink
              key={item.name}
              item={item}
              onClick={onNavigate}
              showLabel={showNavText}
            />
          ))}
        </div>
        {recentRooms.length > 0 && showNavText && (
          <div className="mt-6 border-t border-border/60 pt-4">
            <p className="mb-2 px-2 text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground">
              Recent rooms
            </p>
            <div className="space-y-0.5">
              {recentRooms.map((room) => (
                <Link
                  key={room.id}
                  to={`/rooms/$roomId`}
                  params={{ roomId: room.id }}
                  onClick={onNavigate}
                  className={cn(
                    'flex items-center gap-2 rounded-md px-2 py-2 text-sm transition-colors',
                    location.pathname === `/rooms/${room.id}`
                      ? 'bg-accent font-medium text-accent-foreground'
                      : 'text-muted-foreground hover:bg-accent/80 hover:text-accent-foreground'
                  )}
                >
                  <DoorOpen className="h-4 w-4 shrink-0 opacity-80" />
                  <span className="truncate">{room.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>

      <div className="mt-auto border-t">
        <div
          className={cn(
            'flex items-center p-3',
            showNavText ? 'justify-start' : 'justify-center'
          )}
        >
          <Avatar className="h-8 w-8 shrink-0 border border-border">
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
        </div>
      </div>
    </div>
  );
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth();
  const { presentationMode, togglePresentationMode } = usePresentationMode();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [recentRoomIds, setRecentRoomIds] = React.useState<string[]>([]);
  const location = useLocation();
  const router = useRouter();
  const actionBar = useActionBar();

  const { data: roomsData } = useQuery({
    queryKey: ['rooms'],
    queryFn: () =>
      api.get<{ rooms: Array<{ id: string; name: string }> }>('/api/rooms'),
    enabled: !!user
  });

  const rooms = roomsData?.rooms ?? [];
  const isAdminOrOwner = user?.role === 'owner' || user?.role === 'admin';
  const effectivePresentationMode = presentationMode && isAdminOrOwner;
  const activeRoomId = roomIdFromPathname(location.pathname);
  const recentRooms = React.useMemo(() => {
    const roomById = new Map(rooms.map((room) => [room.id, room]));
    return recentRoomIds.flatMap((roomId) => {
      const room = roomById.get(roomId);
      return room ? [room] : [];
    });
  }, [recentRoomIds, rooms]);

  React.useEffect(() => {
    if (!user?.id) {
      setRecentRoomIds([]);
      return;
    }

    const syncRecentRooms = () => {
      const next = readRecentRoomIds(user.id);
      setRecentRoomIds((current) =>
        stringArraysEqual(current, next) ? current : next
      );
    };

    syncRecentRooms();
    window.addEventListener(RECENT_ROOMS_EVENT, syncRecentRooms);
    window.addEventListener('storage', syncRecentRooms);
    return () => {
      window.removeEventListener(RECENT_ROOMS_EVENT, syncRecentRooms);
      window.removeEventListener('storage', syncRecentRooms);
    };
  }, [user?.id]);

  React.useEffect(() => {
    if (
      !user?.id ||
      effectivePresentationMode ||
      !activeRoomId ||
      !rooms.some((room) => room.id === activeRoomId)
    )
      return;

    const next = recordRecentRoomOpen(user.id, activeRoomId);
    setRecentRoomIds((current) =>
      stringArraysEqual(current, next) ? current : next
    );
  }, [activeRoomId, effectivePresentationMode, rooms, user?.id]);

  if (isPublicAppRoute(location.pathname)) {
    return (
      <div className="h-full min-h-0 w-full overflow-y-auto bg-background">
        {children}
      </div>
    );
  }

  const filteredNav = navItems.filter((item) =>
    item.roles.includes(user?.role ?? 'viewer')
  );
  const visibleRecentRooms = effectivePresentationMode ? [] : recentRooms;

  const initials =
    user?.email
      ?.split('@')[0]
      ?.split('.')
      ?.map((n) => n[0])
      ?.join('')
      ?.toUpperCase() ?? 'U';

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-background">
      <header className="flex h-14 shrink-0 items-center gap-3 border-b bg-card px-4 lg:gap-4 lg:px-6">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen} side="left">
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden">
              <Menu className="h-5 w-5" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent
            className="w-[min(20rem,85vw)] max-w-none border-0 bg-transparent p-0 shadow-none sm:max-w-none"
            showCloseButton={false}
          >
            <SidebarContent
              mode="drawer"
              expanded
              onNavigate={() => setMobileOpen(false)}
              sidebarCollapsed={sidebarCollapsed}
              recentRooms={visibleRecentRooms}
              filteredNav={filteredNav}
              initials={initials}
            />
          </SheetContent>
        </Sheet>

        <Link
          to="/dashboard"
          className="hidden min-w-0 items-center gap-2 lg:flex"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center">
            <AppLogo />
          </div>
          <CompanyName />
        </Link>

        <div className="ml-auto flex items-center gap-2">
          {!!user && (
            <Button
              type="button"
              variant="outline"
              className="hidden h-9 w-[min(14rem,28vw)] justify-start gap-2 px-2.5 text-muted-foreground lg:flex"
              onClick={() => actionBar.open()}
            >
              <span className="truncate">Search…</span>
              <span className="ml-auto inline-flex items-center gap-1 rounded border bg-background px-1.5 py-0.5 text-[0.65rem] text-foreground/70">
                <span className="font-medium">⌘</span>
                <span className="font-medium">K</span>
              </span>
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full"
                aria-label="Open user menu"
              >
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="text-xs">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isAdminOrOwner && (
                <>
                  <DropdownMenuItem onClick={togglePresentationMode}>
                    {presentationMode ? (
                      <EyeOff className="mr-2 h-4 w-4" />
                    ) : (
                      <Eye className="mr-2 h-4 w-4" />
                    )}
                    {presentationMode
                      ? 'Presenter mode off'
                      : 'Presenter mode on'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                onClick={() => router.navigate({ to: '/settings/profile' })}
              >
                <UserIcon className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => router.navigate({ to: '/settings' })}
              >
                <Settings className="mr-2 h-4 w-4" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout}>
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <aside
          className={cn(
            'relative hidden lg:flex flex-col border-r bg-card transition-all duration-300',
            sidebarCollapsed ? 'w-16' : 'w-64'
          )}
        >
          <SidebarContent
            sidebarCollapsed={sidebarCollapsed}
            recentRooms={visibleRecentRooms}
            filteredNav={filteredNav}
            initials={initials}
          />
          <button
            type="button"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute bottom-4 -right-3 z-10 flex h-6 w-6 items-center justify-center rounded-full border bg-background shadow-sm"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="h-3 w-3" />
            ) : (
              <ChevronLeft className="h-3 w-3" />
            )}
          </button>
        </aside>

        <main className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
          {children}
        </main>
      </div>
    </div>
  );
}
